import React, { useState } from "react"
import { Button, Divider, Empty, Form, List, Select, Space, notification } from "antd"
import { DeleteOutlined } from "@ant-design/icons"

import {
    createConditionalRequiredRule,
    deleteConditionalRequiredRule,
} from "../../api/checklists"
import { getAllAmoFields, getAmoEnumDisplayValue } from "../../utils/amoFieldsUtils"

const getSelectableAmoFields = () => {
    return getAllAmoFields().filter(field => {
        const type = String(field.type || "").toUpperCase()
        return (
            ["SELECT", "MULTISELECT"].includes(type) &&
            field.enums &&
            Object.keys(field.enums).length > 0
        )
    })
}

const getAmoEnumLabel = (amoField, value) => {
    return getAmoEnumDisplayValue(amoField?.enums?.[value], value)
}

const getRuleSummary = (rule, amoFieldsById, customFieldsById) => {
    const amoField = amoFieldsById[String(rule.amo_field_id)]
    const fieldName = amoField?.name || `Поле ${rule.amo_field_id}`
    const triggerLabels = (rule.trigger_values || []).map(value =>
        getAmoEnumLabel(amoField, value)
    )
    const excludedFieldNames = (rule.excluded_custom_field_ids || []).map(fieldId =>
        customFieldsById[fieldId]?.name || `Поле ${fieldId}`
    )

    return `Когда "${fieldName}" имеет значение "${triggerLabels.join('", "')}", сделать необязательными: ${excludedFieldNames.join(", ")}`
}

const ConditionalRequiredRulesEditor = ({
    statusId,
    editingChecklist,
    setEditingChecklist,
    checklists,
    setChecklists,
}) => {
    const [selectedAmoFieldId, setSelectedAmoFieldId] = useState()
    const [selectedTriggerValues, setSelectedTriggerValues] = useState([])
    const [selectedExcludedFieldIds, setSelectedExcludedFieldIds] = useState([])
    const [loading, setLoading] = useState(false)

    const availableAmoFields = getSelectableAmoFields()
    const amoFieldsById = availableAmoFields.reduce((acc, field) => {
        acc[String(field.id)] = field
        return acc
    }, {})
    const selectedAmoField = selectedAmoFieldId
        ? amoFieldsById[String(selectedAmoFieldId)]
        : null
    const customFields = editingChecklist?.custom_fields || []
    const customFieldsById = customFields.reduce((acc, field) => {
        acc[field.id] = field
        return acc
    }, {})
    const conditionalRules = editingChecklist?.conditional_required_rules || []

    const syncChecklistRules = (nextRules) => {
        const nextChecklist = {
            ...editingChecklist,
            conditional_required_rules: nextRules,
        }
        setEditingChecklist(nextChecklist)
        setChecklists(
            checklists.map(checklist =>
                checklist.id === nextChecklist.id ? nextChecklist : checklist
            )
        )
    }

    const resetForm = () => {
        setSelectedAmoFieldId(undefined)
        setSelectedTriggerValues([])
        setSelectedExcludedFieldIds([])
    }

    const handleCreateRule = async () => {
        if (!selectedAmoFieldId || selectedTriggerValues.length === 0 || selectedExcludedFieldIds.length === 0) {
            return
        }

        setLoading(true)
        try {
            const createdRule = await createConditionalRequiredRule(
                statusId,
                editingChecklist.id,
                {
                    amo_field_id: selectedAmoFieldId,
                    trigger_values: selectedTriggerValues,
                    excluded_custom_field_ids: selectedExcludedFieldIds,
                }
            )

            if (!createdRule?.id) {
                throw new Error("Не удалось создать правило")
            }

            syncChecklistRules([...conditionalRules, createdRule])
            resetForm()
        } catch (error) {
            notification.error({
                message: "Ошибка",
                description: "Не удалось сохранить правило условной обязательности",
                placement: "bottomRight",
            })
        } finally {
            setLoading(false)
        }
    }

    const handleDeleteRule = async (ruleId) => {
        setLoading(true)
        try {
            const success = await deleteConditionalRequiredRule(
                statusId,
                editingChecklist.id,
                ruleId
            )
            if (!success) {
                throw new Error("Не удалось удалить правило")
            }

            syncChecklistRules(
                conditionalRules.filter(rule => rule.id !== ruleId)
            )
        } catch (error) {
            notification.error({
                message: "Ошибка",
                description: "Не удалось удалить правило условной обязательности",
                placement: "bottomRight",
            })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Space direction="vertical" style={{ width: "100%" }}>
            <List
                bordered
                loading={loading}
                dataSource={conditionalRules}
                locale={{ emptyText: <Empty description="Нет правил условной обязательности" /> }}
                style={{ maxHeight: "220px", overflowY: "auto" }}
                renderItem={(rule) => (
                    <List.Item
                        actions={[
                            <Button
                                key={`delete-${rule.id}`}
                                icon={<DeleteOutlined />}
                                danger
                                size="small"
                                onClick={() => handleDeleteRule(rule.id)}
                            />,
                        ]}
                    >
                        {getRuleSummary(rule, amoFieldsById, customFieldsById)}
                    </List.Item>
                )}
            />

            <Divider style={{ margin: "12px 0" }} />

            <Form layout="vertical">
                <Form.Item label="AMO-поле">
                    <Select
                        placeholder="Выберите селектор или мультисписок"
                        value={selectedAmoFieldId}
                        onChange={(value) => {
                            setSelectedAmoFieldId(value)
                            setSelectedTriggerValues([])
                        }}
                        disabled={loading}
                        options={availableAmoFields.map(field => ({
                            label: `${field.name} (${String(field.type).toUpperCase() === "SELECT" ? "Селектор" : "Мультисписок"})`,
                            value: field.id,
                        }))}
                    />
                </Form.Item>

                <Form.Item label="Значения AMO-поля">
                    <Select
                        mode="multiple"
                        placeholder="Выберите одно или несколько значений"
                        value={selectedTriggerValues}
                        onChange={setSelectedTriggerValues}
                        disabled={loading || !selectedAmoField}
                        options={Object.keys(selectedAmoField?.enums || {}).map(value => ({
                            label: getAmoEnumLabel(selectedAmoField, value),
                            value: String(value),
                        }))}
                    />
                </Form.Item>

                <Form.Item label="Какие кастомные поля сделать необязательными">
                    <Select
                        mode="multiple"
                        placeholder="Выберите поля текущего чеклиста"
                        value={selectedExcludedFieldIds}
                        onChange={setSelectedExcludedFieldIds}
                        disabled={loading || customFields.length === 0}
                        options={customFields.map(field => ({
                            label: field.name,
                            value: field.id,
                        }))}
                    />
                </Form.Item>

                <Button
                    type="primary"
                    block
                    onClick={handleCreateRule}
                    disabled={
                        loading ||
                        !selectedAmoFieldId ||
                        selectedTriggerValues.length === 0 ||
                        selectedExcludedFieldIds.length === 0
                    }
                >
                    Добавить правило
                </Button>
            </Form>
        </Space>
    )
}

export default ConditionalRequiredRulesEditor
